/**
 * UserGuidePage Component
 * AIDLC Administrative Backend
 *
 * Comprehensive user guide for the AIDLC Admin Portal. Covers first-time
 * login, persona-based navigation, dashboard usage, agent management,
 * chat interface, work management tools, and settings.
 *
 * Structure:
 * - Getting Started (what AIDLC is, first login, landing page)
 * - Navigation & Personas (sidebar, 5 personas, collapsible sections)
 * - Dashboard (agent status cards, real-time WebSocket updates)
 * - Agents (14-agent team, profiles, capabilities)
 * - Chat Interface (messaging agents, dispatching, response handling)
 * - Work Management (Sprint Board, Backlog, Feature Tracker, Lifecycle)
 * - Settings (app settings, notifications, theme)
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../shared/Card';
import { Badge } from '../shared/Badge';

// ─── Types ────────────────────────────────────────────────────────

interface Section {
  id: string;
  title: string;
  icon: React.ReactNode;
}

// ─── Constants ────────────────────────────────────────────────────

const SECTIONS: Section[] = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
      </svg>
    ),
  },
  {
    id: 'navigation',
    title: 'Navigation & Personas',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
      </svg>
    ),
  },
  {
    id: 'dashboard',
    title: 'Dashboard',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25a2.25 2.25 0 01-2.25-2.25v-2.25z" />
      </svg>
    ),
  },
  {
    id: 'agents',
    title: 'Agents',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
      </svg>
    ),
  },
  {
    id: 'chat',
    title: 'Chat Interface',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path d="M20.25 8.511c.884.284 1.5 1.128 1.5 2.097v4.286c0 1.136-.847 2.1-1.98 2.193-.34.027-.68.052-1.02.072v3.091l-3-3c-1.354 0-2.694-.055-4.02-.163a2.115 2.115 0 01-.825-.242m9.345-8.334a2.126 2.126 0 00-.476-.095 48.64 48.64 0 00-8.048 0c-1.131.094-1.976 1.057-1.976 2.192v4.286c0 .837.46 1.58 1.155 1.951m9.345-8.334V6.637c0-1.621-1.152-3.026-2.76-3.235A48.455 48.455 0 0011.25 3c-2.115 0-4.198.137-6.24.402-1.608.209-2.76 1.614-2.76 3.235v6.226c0 1.621 1.152 3.026 2.76 3.235.577.075 1.157.14 1.74.194V21l4.155-4.155" />
      </svg>
    ),
  },
  {
    id: 'work-management',
    title: 'Work Management',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path d="M6 6.878V6a2.25 2.25 0 012.25-2.25h7.5A2.25 2.25 0 0118 6v.878m-12 0c.235-.083.487-.128.75-.128h10.5c.263 0 .515.045.75.128m-12 0A2.25 2.25 0 003.75 9v.878m0 0c.235-.083.487-.128.75-.128h10.5c.263 0 .515.045.75.128M3.75 9.878A2.25 2.25 0 001.5 12.25v6.5A2.25 2.25 0 003.75 21h16.5A2.25 2.25 0 0022.5 18.75v-6.5A2.25 2.25 0 0020.25 9.878" />
      </svg>
    ),
  },
  {
    id: 'settings',
    title: 'Settings',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z" />
        <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
  },
];

// ─── Section Content Components ───────────────────────────────────

function GettingStartedSection() {
  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600 leading-relaxed">
        The AIDLC Admin Portal is the command center for managing the AI Development
        Lifecycle (AIDLC) at LDAJA. It provides real-time visibility into agent activity,
        project status, work management, and operational controls -- all accessible from a
        single web interface at{' '}
        <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono">https://ldaja.ai/dashboard</code>.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {[
          {
            title: 'Agent Operations',
            description: 'Monitor, dispatch, and communicate with 14 specialized AI agents in real time via WebSocket.',
            badge: 'Core',
          },
          {
            title: 'Work Management',
            description: 'Plan sprints, manage backlogs, track features, and follow every item through the AIDLC lifecycle.',
            badge: 'Planning',
          },
          {
            title: 'Governance',
            description: 'Enforce code review gates, QA verification, compliance standards, and audit-ready record keeping.',
            badge: 'Quality',
          },
        ].map((feature) => (
          <div key={feature.title} className="bg-gray-50 rounded-lg p-4 border border-gray-100">
            <div className="flex items-center gap-2 mb-2">
              <h4 className="text-sm font-semibold text-ldaja-black">{feature.title}</h4>
              <Badge variant="info">{feature.badge}</Badge>
            </div>
            <p className="text-xs text-gray-500 leading-relaxed">{feature.description}</p>
          </div>
        ))}
      </div>

      <h3 className="text-sm font-semibold text-ldaja-black mt-6">First-Time Login</h3>
      <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
        <ol className="space-y-3 text-xs text-gray-600">
          <li className="flex gap-3">
            <span className="w-5 h-5 rounded-full bg-ldaja-blue/10 flex items-center justify-center flex-shrink-0">
              <span className="text-[10px] font-bold text-ldaja-blue">1</span>
            </span>
            <div>
              <strong className="text-gray-700">Navigate to the portal</strong> at{' '}
              <code className="bg-gray-100 px-1 rounded">https://ldaja.ai/dashboard</code>.
              The address without /dashboard is the public landing page, with company information only.
            </div>
          </li>
          <li className="flex gap-3">
            <span className="w-5 h-5 rounded-full bg-ldaja-blue/10 flex items-center justify-center flex-shrink-0">
              <span className="text-[10px] font-bold text-ldaja-blue">2</span>
            </span>
            <div>
              <strong className="text-gray-700">Sign in when asked</strong>. If you are not signed in,
              the portal shows a sign-in button that takes you to the Microsoft sign-in page.
            </div>
          </li>
          <li className="flex gap-3">
            <span className="w-5 h-5 rounded-full bg-ldaja-blue/10 flex items-center justify-center flex-shrink-0">
              <span className="text-[10px] font-bold text-ldaja-blue">3</span>
            </span>
            <div>
              <strong className="text-gray-700">Sign in with your LDAJA Microsoft 365 account</strong>.
              Multi-factor authentication (MFA) may be required depending on your tenant policy.
            </div>
          </li>
          <li className="flex gap-3">
            <span className="w-5 h-5 rounded-full bg-ldaja-blue/10 flex items-center justify-center flex-shrink-0">
              <span className="text-[10px] font-bold text-ldaja-blue">4</span>
            </span>
            <div>
              <strong className="text-gray-700">You land on the Agent Dashboard</strong> (the default
              home page). Agent status cards load in real time via WebSocket. You are now ready to work.
            </div>
          </li>
        </ol>
      </div>

      <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
        <h4 className="text-xs font-semibold text-blue-800 mb-1">Development Mode</h4>
        <p className="text-xs text-blue-700 leading-relaxed">
          When running locally with{' '}
          <code className="bg-blue-100 px-1 rounded">VITE_DEV_AUTH_BYPASS=true</code> in your{' '}
          <code className="bg-blue-100 px-1 rounded">.env</code> file, MSAL login is skipped in the portal.
          The backend accepts that only with{' '}
          <code className="bg-blue-100 px-1 rounded">AUTH_DEV_BYPASS=true</code> as well, and only on this
          computer; a red banner shows across the portal while either is on. Neither may ever be set to{' '}
          <code className="bg-blue-100 px-1 rounded">true</code> in production.
        </p>
      </div>

      <Card title="Landing Page Orientation" subtitle="What you see after login">
        <div className="space-y-2 text-xs text-gray-600">
          <div className="flex items-center gap-3">
            <Badge variant="info">Left</Badge>
            <span>Collapsible sidebar with persona-filtered navigation and section groups</span>
          </div>
          <div className="flex items-center gap-3">
            <Badge variant="info">Top</Badge>
            <span>Header bar with breadcrumbs, persona selector dropdown, and notification bell</span>
          </div>
          <div className="flex items-center gap-3">
            <Badge variant="info">Center</Badge>
            <span>Main content area showing the Agent Dashboard with real-time status cards</span>
          </div>
        </div>
      </Card>
    </div>
  );
}

function NavigationSection() {
  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600 leading-relaxed">
        The sidebar is the primary navigation tool. It organizes all portal pages into
        collapsible sections and filters visibility based on the active persona. Switching
        personas changes which sections and pages are visible, so each user sees only
        the tools relevant to their role.
      </p>

      <h3 className="text-sm font-semibold text-ldaja-black">Sidebar Basics</h3>
      <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
        <ol className="space-y-3 text-xs text-gray-600">
          <li className="flex gap-3">
            <span className="w-5 h-5 rounded-full bg-ldaja-blue/10 flex items-center justify-center flex-shrink-0">
              <span className="text-[10px] font-bold text-ldaja-blue">1</span>
            </span>
            <div>
              <strong className="text-gray-700">Collapse or expand the sidebar</strong> using the
              hamburger icon at the top left. In collapsed mode, only icons are visible; hover to
              see tooltips with page names.
            </div>
          </li>
          <li className="flex gap-3">
            <span className="w-5 h-5 rounded-full bg-ldaja-blue/10 flex items-center justify-center flex-shrink-0">
              <span className="text-[10px] font-bold text-ldaja-blue">2</span>
            </span>
            <div>
              <strong className="text-gray-700">Click a section header</strong> (e.g., &quot;Operations&quot;,
              &quot;Development&quot;) to expand or collapse that group of pages. Sections remember their
              open/closed state during your session.
            </div>
          </li>
          <li className="flex gap-3">
            <span className="w-5 h-5 rounded-full bg-ldaja-blue/10 flex items-center justify-center flex-shrink-0">
              <span className="text-[10px] font-bold text-ldaja-blue">3</span>
            </span>
            <div>
              <strong className="text-gray-700">Click a page link</strong> within an expanded section
              to navigate. The active page is highlighted with a blue left border and bold text.
            </div>
          </li>
        </ol>
      </div>

      <h3 className="text-sm font-semibold text-ldaja-black mt-6">The 5 Personas</h3>
      <p className="text-xs text-gray-500 leading-relaxed mb-3">
        Personas control which sidebar sections and pages are visible. Switch personas using the
        dropdown in the header. Each persona is designed for a different audience and workflow.
      </p>

      <Card title="Persona Reference" subtitle="What each persona sees and who it serves">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gray-50">
                <th className="text-left px-3 py-2 font-semibold text-gray-700">Persona</th>
                <th className="text-left px-3 py-2 font-semibold text-gray-700">Audience</th>
                <th className="text-left px-3 py-2 font-semibold text-gray-700">Visible Sections</th>
                <th className="text-left px-3 py-2 font-semibold text-gray-700">Focus</th>
              </tr>
            </thead>
            <tbody>
              {[
                {
                  persona: 'Executive',
                  audience: 'CEO, stakeholders',
                  sections: 'Overview, Projects Overview, Budget, Roadmap',
                  focus: 'High-level status, budget health, milestone progress',
                },
                {
                  persona: 'Ops',
                  audience: 'Director of Operations',
                  sections: 'All sections visible',
                  focus: 'Full portal access -- agent management, deployment, email, settings',
                },
                {
                  persona: 'Orchestrator',
                  audience: 'Project managers, scrum masters',
                  sections: 'Sprint Board, Backlog, Features, Chat, Lifecycle',
                  focus: 'Work planning, sprint execution, agent coordination',
                },
                {
                  persona: 'Builder',
                  audience: 'Developers, agents',
                  sections: 'Chat, Backlog, Code Review, Templates, Training',
                  focus: 'Code writing, task execution, code review pipeline',
                },
                {
                  persona: 'Gatekeeper',
                  audience: 'QA, compliance, security',
                  sections: 'Governance, Audit, DoD Pipeline, Assessments, Reviews',
                  focus: 'Quality gates, compliance checks, review approval workflows',
                },
              ].map((row) => (
                <tr key={row.persona} className="border-b border-gray-50">
                  <td className="px-3 py-2 font-medium text-gray-700">
                    <Badge variant="purple">{row.persona}</Badge>
                  </td>
                  <td className="px-3 py-2 text-gray-500">{row.audience}</td>
                  <td className="px-3 py-2 text-gray-500">{row.sections}</td>
                  <td className="px-3 py-2 text-gray-500">{row.focus}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <h4 className="text-xs font-semibold text-amber-800 mb-1">Tip: Ops Persona Shows Everything</h4>
        <p className="text-xs text-amber-700 leading-relaxed">
          If you are unsure which persona to use, select <strong>Ops</strong>. It is the only persona
          that shows every sidebar section and page. You can always switch to a more focused persona
          later without losing any data or state.
        </p>
      </div>
    </div>
  );
}

function DashboardSection() {
  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600 leading-relaxed">
        The Agent Dashboard is the default landing page and the operational heart of the portal.
        It displays real-time status cards for every agent in the AIDLC team, updated live via
        WebSocket connections to the backend.
      </p>

      <h3 className="text-sm font-semibold text-ldaja-black">Agent Status Cards</h3>
      <p className="text-xs text-gray-500 leading-relaxed mb-3">
        Each agent is represented by a card showing their name, role, current status, and last
        activity timestamp. Cards update automatically as agents report status changes through the
        bridge system.
      </p>

      <Card title="Agent Status Reference" subtitle="What each status indicator means">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gray-50">
                <th className="text-left px-3 py-2 font-semibold text-gray-700">Status</th>
                <th className="text-left px-3 py-2 font-semibold text-gray-700">Indicator</th>
                <th className="text-left px-3 py-2 font-semibold text-gray-700">Meaning</th>
                <th className="text-left px-3 py-2 font-semibold text-gray-700">Typical Duration</th>
              </tr>
            </thead>
            <tbody>
              {[
                {
                  status: 'Available',
                  indicator: 'success',
                  meaning: 'Agent is idle and ready to accept new tasks. No active work in progress.',
                  duration: 'Until dispatched',
                },
                {
                  status: 'Working',
                  indicator: 'info',
                  meaning: 'Agent is actively executing a task. The task description is displayed on the card.',
                  duration: 'Seconds to minutes',
                },
                {
                  status: 'Idle',
                  indicator: 'warning',
                  meaning: 'Agent has not reported status recently. May need attention or restart.',
                  duration: 'Varies',
                },
                {
                  status: 'Error',
                  indicator: 'danger',
                  meaning: 'Agent encountered a failure during task execution. Check the task details for the error message.',
                  duration: 'Until resolved',
                },
              ].map((row) => (
                <tr key={row.status} className="border-b border-gray-50">
                  <td className="px-3 py-2 font-medium text-gray-700">{row.status}</td>
                  <td className="px-3 py-2">
                    <Badge variant={row.indicator as 'success' | 'info' | 'warning' | 'danger'} dot>
                      {row.status}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 text-gray-500">{row.meaning}</td>
                  <td className="px-3 py-2 text-gray-400">{row.duration}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <h3 className="text-sm font-semibold text-ldaja-black mt-6">Real-Time Updates</h3>
      <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
        <div className="space-y-3">
          {[
            {
              title: 'WebSocket Connection',
              description: 'The portal maintains a persistent WebSocket connection to the backend (Socket.io). Agent status changes are pushed to all connected clients instantly.',
            },
            {
              title: 'Bridge System',
              description: 'Agents report status via the bridge scripts (report.js). The backend watches for bridge file changes using fs.watchFile, parses updates, stores them in the in-memory store, and broadcasts via WebSocket.',
            },
            {
              title: 'Automatic Reconnection',
              description: 'If the WebSocket connection drops (network issue, server restart), Socket.io automatically attempts reconnection with exponential backoff. Status cards will refresh once the connection is restored.',
            },
            {
              title: 'Connection Indicator',
              description: 'The header displays a green dot when the WebSocket is connected and a red dot when disconnected. If you see a red dot, agent status may be stale.',
            },
          ].map((feature) => (
            <div key={feature.title} className="flex gap-3">
              <svg className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <h4 className="text-xs font-semibold text-gray-700">{feature.title}</h4>
                <p className="text-xs text-gray-500 mt-0.5">{feature.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
        <h4 className="text-xs font-semibold text-blue-800 mb-1">How It Works Under the Hood</h4>
        <p className="text-xs text-blue-700 leading-relaxed">
          Agent status flows through a three-step pipeline: (1) an agent or the orchestrator calls{' '}
          <code className="bg-blue-100 px-1 rounded">report.js --agent X --status working --task &quot;Y&quot;</code>,
          (2) the backend detects the file change and updates the in-memory store (Cosmos DB-ready schema),
          and (3) the store broadcasts the update via Socket.io to every connected dashboard client. End-to-end
          latency is typically under 500ms on a local network.
        </p>
      </div>
    </div>
  );
}

/**
 * The agent team as the guide describes it: one row per roster agent (agents/<id>.md and
 * server/src/data/seedAgents.ts; server/test/dispatcher.test.ts checks the ids agree).
 */
const AGENT_TEAM = [
  { id: 'dan', name: 'Dan', role: 'Director of Operations', domain: 'Operations', duties: 'Orchestration, agent coordination, process enforcement, command center' },
  { id: 'marcus', name: 'Marcus', role: 'Senior Full-Stack Developer', domain: 'Development', duties: 'Features end to end, bug fixes with root cause, code complete for review' },
  { id: 'bill', name: 'Bill', role: 'Principal Code Reviewer & OWASP SME', domain: 'Quality', duties: 'Code review (+2 required), OWASP Top 10 scan, standards enforcement' },
  { id: 'asha', name: 'Asha', role: 'QA Test Engineer & Playwright SME', domain: 'Quality', duties: 'Full user-flow testing, QA pass/fail gate, Playwright evidence, bug reports' },
  { id: 'todd', name: 'Todd', role: 'Network Administrator', domain: 'Infrastructure', duties: 'Azure, Entra ID, App Service, Intune, Defender, network security' },
  { id: 'raj', name: 'Raj', role: 'Database Architect', domain: 'Data', duties: 'Schema design, Cosmos DB, indexes, query cost, data migrations' },
  { id: 'maya', name: 'Maya', role: 'UI/UX Architect', domain: 'Design', duties: 'Interface design, user flows, design system, accessibility' },
  { id: 'sarah', name: 'Sarah', role: 'Compliance & Legal Advisor', domain: 'Governance', duties: 'Privacy policy, SOC 2 controls, GDPR/CCPA, contract wording, claims review' },
  { id: 'elena', name: 'Elena', role: 'Technical Writer', domain: 'Documentation', duties: 'User guides, API docs, architecture docs, release notes' },
  { id: 'carlos', name: 'Carlos', role: 'Mobile Developer', domain: 'Development', duties: 'React Native / Expo, iOS and Android, App Store and Play Store releases' },
  { id: 'priya', name: 'Priya', role: 'Performance Engineer', domain: 'Infrastructure', duties: 'Load testing, profiling, monitoring and alerting, scalability planning' },
  { id: 'james', name: 'James', role: 'DevOps Engineer', domain: 'Infrastructure', duties: 'CI/CD pipelines, containers, GitHub Actions / Azure DevOps, Terraform' },
  { id: 'mei', name: 'Mei', role: 'Data Analyst', domain: 'Analytics', duties: 'Usage metrics, KPIs, dashboards, data analysis and reporting' },
  { id: 'harper', name: 'Harper', role: 'HR Director & Talent Acquisition', domain: 'Operations', duties: 'Agent hiring, persona review, team composition' },
  { id: 'alex', name: 'Alex', role: 'Solutions Architect', domain: 'Development', duties: "Owns How it's built in every spec: data flow, caching, failure modes, budgets, test seams, rollout" },
  { id: 'sam', name: 'Sam', role: 'Scrum Master', domain: 'Planning', duties: 'Standups, sprint planning, backlog grooming, velocity tracking' },
  { id: 'maureen', name: 'Maureen', role: 'Technical Trainer & Documentation Specialist', domain: 'Documentation', duties: 'Walkthroughs, training material, onboarding guides, architecture diagrams' },
  { id: 'bedsy', name: 'Bedsy', role: 'Hockey Domain Consultant', domain: 'Domain', duties: 'Hockey terminology, rules and real coach/parent workflows for Rink Rater and GTHA' },
  { id: 'gregory', name: 'Gregory', role: 'HIPAA & SOC Compliance Auditor', domain: 'Governance', duties: 'HIPAA and SOC 2 rulings, PHI/PII governance for the Compliance Center' },
  { id: 'nina', name: 'Nina', role: 'Head of Marketing', domain: 'Marketing', duties: 'Positioning, go-to-market, campaigns, brand strategy' },
  { id: 'jamie', name: 'Jamie', role: 'Marketing Copy Writer', domain: 'Marketing', duties: 'Product messaging, onboarding copy, UI microcopy, US English' },
  { id: 'kim', name: 'Kim', role: 'Product Manager', domain: 'Product', duties: 'Runs the requirements intake as an interview: journey, success metrics, priorities, AI guardrails; hands off to Gregory, Maya, Alex' },
];

function AgentsSection() {
  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600 leading-relaxed">
        The AIDLC system operates with a team of {AGENT_TEAM.length} specialized agents, each responsible for a
        specific domain within the software development lifecycle. Every agent has a defined role,
        skill set, and set of responsibilities enforced through their agent instruction files.
      </p>

      <Card title="The AIDLC Agent Team" subtitle={`${AGENT_TEAM.length} agents covering the full development lifecycle`}>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gray-50">
                <th className="text-left px-3 py-2 font-semibold text-gray-700">Agent</th>
                <th className="text-left px-3 py-2 font-semibold text-gray-700">Role</th>
                <th className="text-left px-3 py-2 font-semibold text-gray-700">Domain</th>
                <th className="text-left px-3 py-2 font-semibold text-gray-700">Key Responsibilities</th>
              </tr>
            </thead>
            <tbody>
              {AGENT_TEAM.map((agent) => (
                <tr key={agent.id} className="border-b border-gray-50">
                  <td className="px-3 py-2 font-medium text-gray-700">{agent.name}</td>
                  <td className="px-3 py-2 text-gray-500">{agent.role}</td>
                  <td className="px-3 py-2">
                    <Badge variant={
                      agent.domain === 'Operations' ? 'info' :
                      agent.domain === 'Development' ? 'purple' :
                      agent.domain === 'Quality' ? 'success' :
                      agent.domain === 'Design' ? 'info' :
                      agent.domain === 'Infrastructure' ? 'warning' :
                      agent.domain === 'Data' ? 'purple' :
                      agent.domain === 'Governance' ? 'danger' :
                      'neutral'
                    }>
                      {agent.domain}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 text-gray-500">{agent.duties}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <h3 className="text-sm font-semibold text-ldaja-black mt-6">Viewing Agent Profiles</h3>
      <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
        <ol className="space-y-3 text-xs text-gray-600">
          <li className="flex gap-3">
            <span className="w-5 h-5 rounded-full bg-ldaja-blue/10 flex items-center justify-center flex-shrink-0">
              <span className="text-[10px] font-bold text-ldaja-blue">1</span>
            </span>
            <div>
              <strong className="text-gray-700">Click an agent&apos;s name or card</strong> on the
              Dashboard to open their profile page at{' '}
              <code className="bg-gray-100 px-1 rounded">/agents/:id</code>.
            </div>
          </li>
          <li className="flex gap-3">
            <span className="w-5 h-5 rounded-full bg-ldaja-blue/10 flex items-center justify-center flex-shrink-0">
              <span className="text-[10px] font-bold text-ldaja-blue">2</span>
            </span>
            <div>
              <strong className="text-gray-700">Review agent details</strong> including their role
              description, current status, capabilities, instruction file content, and recent
              activity history.
            </div>
          </li>
          <li className="flex gap-3">
            <span className="w-5 h-5 rounded-full bg-ldaja-blue/10 flex items-center justify-center flex-shrink-0">
              <span className="text-[10px] font-bold text-ldaja-blue">3</span>
            </span>
            <div>
              <strong className="text-gray-700">Dispatch a task</strong> directly from the profile
              page by clicking the action button, or navigate to the Chat Interface to have a
              conversation with the agent.
            </div>
          </li>
        </ol>
      </div>

      <Card title="Agent Capabilities">
        <div className="space-y-3">
          {[
            {
              title: 'Task Execution',
              description: 'Agents can be dispatched to perform specific tasks. Each agent operates within its domain and follows its instruction file (.md) for behavior, tone, and output standards.',
            },
            {
              title: 'Chat Interaction',
              description: 'Agents can be engaged in conversational chat via the Chat Interface. Messages are dispatched through the agent-dispatcher bridge to Claude Code and streamed back.',
            },
            {
              title: 'Status Reporting',
              description: 'Agents report their status (available, working, idle, error) via the bridge. This status is reflected in real time on the dashboard.',
            },
            {
              title: 'Pipeline Participation',
              description: 'Certain agents participate in hard gates. Bill (Code Review) must provide a +2 approval. Asha (QA) must provide a PASS. Both are required before any commit.',
            },
          ].map((capability) => (
            <div key={capability.title} className="flex gap-3">
              <svg className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <h4 className="text-xs font-semibold text-gray-700">{capability.title}</h4>
                <p className="text-xs text-gray-500 mt-0.5">{capability.description}</p>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function ChatSection() {
  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600 leading-relaxed">
        The Chat Interface is the primary way to interact with AIDLC agents. It provides a
        conversational UI where you can send messages, receive agent responses, and dispatch
        tasks -- all from a single page at{' '}
        <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono">/chat</code>.
      </p>

      <h3 className="text-sm font-semibold text-ldaja-black">Sending a Message</h3>
      <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
        <ol className="space-y-3 text-xs text-gray-600">
          <li className="flex gap-3">
            <span className="w-5 h-5 rounded-full bg-ldaja-blue/10 flex items-center justify-center flex-shrink-0">
              <span className="text-[10px] font-bold text-ldaja-blue">1</span>
            </span>
            <div>
              <strong className="text-gray-700">Select an agent</strong> from the agent selector at
              the top of the chat panel. This determines which agent will receive and respond to
              your message.
            </div>
          </li>
          <li className="flex gap-3">
            <span className="w-5 h-5 rounded-full bg-ldaja-blue/10 flex items-center justify-center flex-shrink-0">
              <span className="text-[10px] font-bold text-ldaja-blue">2</span>
            </span>
            <div>
              <strong className="text-gray-700">Type your message</strong> in the input field at the
              bottom. Messages can range from quick questions to detailed task instructions.
            </div>
          </li>
          <li className="flex gap-3">
            <span className="w-5 h-5 rounded-full bg-ldaja-blue/10 flex items-center justify-center flex-shrink-0">
              <span className="text-[10px] font-bold text-ldaja-blue">3</span>
            </span>
            <div>
              <strong className="text-gray-700">Press Enter or click Send</strong>. The message is
              dispatched to the selected agent via the agent-dispatcher bridge. The agent&apos;s
              status card on the Dashboard will update to &quot;Working&quot;.
            </div>
          </li>
          <li className="flex gap-3">
            <span className="w-5 h-5 rounded-full bg-ldaja-blue/10 flex items-center justify-center flex-shrink-0">
              <span className="text-[10px] font-bold text-ldaja-blue">4</span>
            </span>
            <div>
              <strong className="text-gray-700">Wait for the response</strong>. The agent processes
              your message through Claude Code and returns its response. The agent status reverts
              to &quot;Available&quot; once the response is complete.
            </div>
          </li>
        </ol>
      </div>

      <h3 className="text-sm font-semibold text-ldaja-black mt-6">Message Flow Architecture</h3>
      <Card title="How Messages Travel" subtitle="From your keyboard to the agent and back">
        <div className="space-y-3">
          {[
            { step: 'User Input', detail: 'You type a message and select a target agent in the Chat Interface.' },
            { step: 'API Request', detail: 'The frontend sends a POST to /api/chat with the message and agent name.' },
            { step: 'Agent Dispatcher', detail: 'The backend invokes agent-dispatcher.js, which spawns a Claude Code session with the agent\'s instruction file loaded.' },
            { step: 'Claude Code Execution', detail: 'Claude Code processes the prompt with the agent\'s persona, context, and the AIDLC process framework.' },
            { step: 'Response Capture', detail: 'The dispatcher captures the agent\'s output and writes it to the bridge. The backend reads it and stores the conversation.' },
            { step: 'WebSocket Delivery', detail: 'The response is pushed to the frontend via WebSocket and displayed in the chat thread. Agent status returns to Available.' },
          ].map((item, idx) => (
            <div key={idx} className="flex items-start gap-3">
              <span className="w-5 h-5 rounded-full bg-ldaja-blue/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-[10px] font-bold text-ldaja-blue">{idx + 1}</span>
              </span>
              <div>
                <h4 className="text-xs font-semibold text-gray-700">{item.step}</h4>
                <p className="text-xs text-gray-500 mt-0.5">{item.detail}</p>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <h3 className="text-sm font-semibold text-ldaja-black mt-6">Tips for Effective Communication</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {[
          {
            title: 'Be Specific',
            description: 'Include file paths, component names, and expected behavior. Agents work best with clear, concrete instructions.',
            badge: 'Best Practice',
          },
          {
            title: 'Use the Right Agent',
            description: 'Send code questions to Marcus, UI feedback to Maya, testing requests to Asha. Each agent excels in their domain.',
            badge: 'Routing',
          },
          {
            title: 'One Task at a Time',
            description: 'For complex requests, break them into separate messages. This allows the agent to focus and produce better results.',
            badge: 'Efficiency',
          },
          {
            title: 'Review Before Sending',
            description: 'Agent dispatch costs tokens. A clear, well-formed message avoids follow-up corrections and saves resources.',
            badge: 'Cost',
          },
        ].map((tip) => (
          <div key={tip.title} className="bg-gray-50 rounded-lg p-4 border border-gray-100">
            <div className="flex items-center gap-2 mb-2">
              <h4 className="text-sm font-semibold text-ldaja-black">{tip.title}</h4>
              <Badge variant="info">{tip.badge}</Badge>
            </div>
            <p className="text-xs text-gray-500 leading-relaxed">{tip.description}</p>
          </div>
        ))}
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <h4 className="text-xs font-semibold text-amber-800 mb-1">Note: Response Time</h4>
        <p className="text-xs text-amber-700 leading-relaxed">
          Agent responses typically take 10-60 seconds depending on the complexity of the request
          and the length of the agent&apos;s context window. The current implementation uses{' '}
          <code className="bg-amber-100 px-1 rounded">claude -p</code> which buffers output.
          True real-time streaming during generation is planned for a future release using{' '}
          <code className="bg-amber-100 px-1 rounded">--output-format stream-json</code>.
        </p>
      </div>
    </div>
  );
}

function WorkManagementSection() {
  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600 leading-relaxed">
        The AIDLC Admin Portal includes a full suite of work management tools for planning,
        tracking, and executing software development work. These tools follow the AIDLC
        (AI Development Lifecycle) methodology and are tightly integrated with the agent team.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {[
          {
            title: 'Sprint Board',
            path: '/projects',
            description: 'Kanban-style board for managing the current sprint. Columns represent workflow stages: To Do, In Progress, In Review, Done. Drag and drop cards between columns as work progresses.',
            badge: 'Active Sprint',
            highlights: [
              'Drag-and-drop task cards',
              'Column-based workflow stages',
              'Assignee and priority tags',
              'Sprint goal and velocity tracking',
            ],
          },
          {
            title: 'Backlog',
            path: '/backlog',
            description: 'The master list of all work items not yet committed to a sprint. Items are prioritized, categorized, and estimated. Use this page to groom, prioritize, and schedule work for upcoming sprints.',
            badge: 'Planning',
            highlights: [
              'Priority-sorted item list',
              'Category and label filtering',
              'Effort estimation fields',
              'Promote items to sprint',
            ],
          },
          {
            title: 'Feature Tracker',
            path: '/features',
            description: 'High-level view of all features under development. Each feature groups related backlog items and tracks overall completion percentage, target milestone, and responsible agents.',
            badge: 'Features',
            highlights: [
              'Feature completion progress bars',
              'Linked backlog items',
              'Milestone targeting',
              'Owner and agent assignments',
            ],
          },
          {
            title: 'AIDLC Lifecycle',
            path: '/lifecycle',
            description: 'Visual representation of the AIDLC process stages. Shows where each work item sits in the lifecycle: Ideation, Specification, Development, Review, QA, Deployment, Monitoring.',
            badge: 'Process',
            highlights: [
              'Stage-by-stage pipeline view',
              'Item progression tracking',
              'Gate requirements per stage',
              'Historical lifecycle analytics',
            ],
          },
        ].map((tool) => (
          <div key={tool.title} className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <h4 className="text-sm font-semibold text-ldaja-black">{tool.title}</h4>
              <Badge variant="info">{tool.badge}</Badge>
            </div>
            <p className="text-xs text-gray-500 leading-relaxed mb-3">{tool.description}</p>
            <ul className="space-y-1">
              {tool.highlights.map((h) => (
                <li key={h} className="flex items-center gap-2 text-xs text-gray-400">
                  <svg className="w-3 h-3 text-green-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path d="M4.5 12.75l6 6 9-13.5" />
                  </svg>
                  {h}
                </li>
              ))}
            </ul>
            <div className="mt-3 text-right">
              <code className="text-[10px] text-gray-400 font-mono">{tool.path}</code>
            </div>
          </div>
        ))}
      </div>

      <h3 className="text-sm font-semibold text-ldaja-black mt-6">Definition of Done (DoD) Pipeline</h3>
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-xs text-red-700 leading-relaxed mb-3">
          Every piece of work must pass through the Definition of Done pipeline before it can be
          committed and deployed. This is a <strong>hard gate</strong> enforced by the orchestrator (Dan).
          No exceptions, no shortcuts, no &quot;we&apos;ll fix it later.&quot;
        </p>
        <ol className="space-y-2 text-xs text-red-700">
          <li className="flex gap-2">
            <span className="font-bold">1.</span>
            <strong>Code Complete</strong> -- The implementing agent finishes the work and marks it ready for review.
          </li>
          <li className="flex gap-2">
            <span className="font-bold">2.</span>
            <strong>Bill (Code Review)</strong> -- Reviews all changed files. A +2 approval is required to proceed.
          </li>
          <li className="flex gap-2">
            <span className="font-bold">3.</span>
            <strong>Asha (QA)</strong> -- Runs test verification on all changed files. A PASS verdict is required.
          </li>
          <li className="flex gap-2">
            <span className="font-bold">4.</span>
            <strong>Commit</strong> -- Only after Bill +2 and Asha PASS does the orchestrator create the git commit.
          </li>
        </ol>
      </div>

      <Card title="Additional Work Management Pages" subtitle="Specialized tools for specific workflows">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gray-50">
                <th className="text-left px-3 py-2 font-semibold text-gray-700">Page</th>
                <th className="text-left px-3 py-2 font-semibold text-gray-700">Route</th>
                <th className="text-left px-3 py-2 font-semibold text-gray-700">Purpose</th>
              </tr>
            </thead>
            <tbody>
              {[
                { page: 'Projects Overview', route: '/projects/overview', purpose: 'Executive-level project summary with status, health, and milestone overview' },
                { page: 'Milestone Tracker', route: '/milestones', purpose: 'Track project milestones, due dates, and completion across the roadmap' },
                { page: 'Recurring Tasks', route: '/recurring-tasks', purpose: 'Manage and schedule recurring work items (standups, reviews, releases)' },
                { page: 'Review Reports', route: '/review-reports', purpose: 'View code review and QA reports generated by Bill and Asha' },
                { page: 'DoD Pipeline', route: '/dod-pipeline', purpose: 'Visual pipeline showing items in each DoD stage with pass/fail indicators' },
                { page: 'Audit & History', route: '/audit', purpose: 'Comprehensive audit trail of all actions, decisions, and state changes' },
              ].map((row) => (
                <tr key={row.page} className="border-b border-gray-50">
                  <td className="px-3 py-2 font-medium text-gray-700">{row.page}</td>
                  <td className="px-3 py-2 font-mono text-ldaja-blue">{row.route}</td>
                  <td className="px-3 py-2 text-gray-500">{row.purpose}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function SettingsSection() {
  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600 leading-relaxed">
        The Settings page at{' '}
        <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono">/settings</code>{' '}
        provides configuration options for the portal experience, notification preferences,
        and display preferences. Settings are persisted per-user and take effect immediately.
      </p>

      <Card title="Application Settings" subtitle="General portal configuration">
        <div className="space-y-3">
          {[
            {
              title: 'Persona Default',
              description: 'Set which persona the portal loads with on login. Useful if you always work in one role (e.g., Ops) and want to skip the persona selector on every visit.',
            },
            {
              title: 'Sidebar Behavior',
              description: 'Choose whether the sidebar starts expanded or collapsed. On narrow screens, collapsed mode saves space; on wide screens, expanded mode shows all navigation labels.',
            },
            {
              title: 'Dashboard Refresh Rate',
              description: 'Control how frequently the dashboard polls for status updates in addition to WebSocket push events. Default is real-time (WebSocket only). Polling can be enabled as a fallback.',
            },
          ].map((setting) => (
            <div key={setting.title} className="flex gap-3">
              <svg className="w-4 h-4 text-ldaja-blue flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path d="M10.5 6h9.75M10.5 6a1.5 1.5 0 11-3 0m3 0a1.5 1.5 0 10-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-9.75 0h9.75" />
              </svg>
              <div>
                <h4 className="text-xs font-semibold text-gray-700">{setting.title}</h4>
                <p className="text-xs text-gray-500 mt-0.5">{setting.description}</p>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Card title="Notification Preferences" subtitle="Control how you receive alerts">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gray-50">
                <th className="text-left px-3 py-2 font-semibold text-gray-700">Notification Type</th>
                <th className="text-left px-3 py-2 font-semibold text-gray-700">Channel</th>
                <th className="text-left px-3 py-2 font-semibold text-gray-700">Description</th>
                <th className="text-left px-3 py-2 font-semibold text-gray-700">Default</th>
              </tr>
            </thead>
            <tbody>
              {[
                {
                  type: 'Agent Error',
                  channel: 'Push + In-App',
                  description: 'Fired when any agent reports an error status. Includes the agent name and error details.',
                  default: 'On',
                },
                {
                  type: 'Deployment Complete',
                  channel: 'In-App',
                  description: 'Notification when a deployment to Azure App Service completes (success or failure).',
                  default: 'On',
                },
                {
                  type: 'Review Required',
                  channel: 'Push + In-App',
                  description: 'Triggered when a code review or QA pass is needed before a commit can proceed.',
                  default: 'On',
                },
                {
                  type: 'Chat Response',
                  channel: 'In-App',
                  description: 'Notification when an agent finishes processing a chat message and the response is ready.',
                  default: 'On',
                },
                {
                  type: 'Sprint Updates',
                  channel: 'In-App',
                  description: 'Daily digest of sprint progress, items moved, and velocity metrics.',
                  default: 'Off',
                },
              ].map((row) => (
                <tr key={row.type} className="border-b border-gray-50">
                  <td className="px-3 py-2 font-medium text-gray-700">{row.type}</td>
                  <td className="px-3 py-2 text-gray-500">{row.channel}</td>
                  <td className="px-3 py-2 text-gray-500">{row.description}</td>
                  <td className="px-3 py-2">
                    <Badge variant={row.default === 'On' ? 'success' : 'neutral'}>
                      {row.default}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card title="Theme & Display" subtitle="Visual customization options">
        <div className="space-y-3">
          {[
            {
              title: 'Color Theme',
              description: 'Currently the portal uses the LDAJA brand theme: black (#000000), white (#FFFFFF), blue (#2563EB), and cyan (#7DD3FC). Dark mode is on the feature roadmap.',
            },
            {
              title: 'Compact Mode',
              description: 'Reduces padding and font sizes across all pages to show more information per screen. Useful for users with smaller displays or who prefer density over whitespace.',
            },
            {
              title: 'Animation Preferences',
              description: 'Toggle transition animations on sidebar, cards, and status changes. Disabling animations respects prefers-reduced-motion and can improve perceived performance.',
            },
          ].map((option) => (
            <div key={option.title} className="flex gap-3">
              <svg className="w-4 h-4 text-ldaja-blue flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path d="M4.098 19.902a3.75 3.75 0 005.304 0l6.401-6.402M6.75 21A3.75 3.75 0 013 17.25V4.125C3 3.504 3.504 3 4.125 3h5.25c.621 0 1.125.504 1.125 1.125v4.072M6.75 21a3.75 3.75 0 003.75-3.75V8.197M6.75 21h13.125c.621 0 1.125-.504 1.125-1.125v-5.25c0-.621-.504-1.125-1.125-1.125h-4.072M10.5 8.197l2.88-2.88c.438-.439 1.15-.439 1.59 0l3.712 3.713c.44.44.44 1.152 0 1.59l-2.879 2.88M6.75 17.25h.008v.008H6.75v-.008z" />
              </svg>
              <div>
                <h4 className="text-xs font-semibold text-gray-700">{option.title}</h4>
                <p className="text-xs text-gray-500 mt-0.5">{option.description}</p>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
        <h4 className="text-xs font-semibold text-blue-800 mb-1">Push Notifications Setup</h4>
        <p className="text-xs text-blue-700 leading-relaxed">
          Push notifications require a one-time browser permission grant. When you first enable
          push notifications in Settings, your browser will prompt you to allow notifications from{' '}
          <code className="bg-blue-100 px-1 rounded">ldaja.ai</code>. The portal registers a
          Service Worker and subscribes via the Web Push API with a VAPID key from the backend.
          Notifications work even when the portal tab is not in focus.
        </p>
      </div>
    </div>
  );
}

// ─── Section Renderer ─────────────────────────────────────────────

const SECTION_COMPONENTS: Record<string, () => JSX.Element> = {
  'getting-started': GettingStartedSection,
  navigation: NavigationSection,
  dashboard: DashboardSection,
  agents: AgentsSection,
  chat: ChatSection,
  'work-management': WorkManagementSection,
  settings: SettingsSection,
};

// ─── Main Component ───────────────────────────────────────────────

export function UserGuidePage() {
  const [activeSection, setActiveSection] = useState('getting-started');
  const navigate = useNavigate();

  const ActiveContent = SECTION_COMPONENTS[activeSection];

  return (
    <div className="space-y-6 max-w-5xl">
      {/* Page Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <button
              onClick={() => navigate('/docs')}
              className="text-xs text-ldaja-blue hover:underline"
            >
              Documentation
            </button>
            <span className="text-xs text-gray-400">/</span>
            <span className="text-xs text-gray-500">User Guide</span>
          </div>
          <h1 className="text-xl font-bold text-ldaja-black">AIDLC Admin Portal User Guide</h1>
          <p className="text-sm text-gray-500 mt-1">
            Complete guide to navigating and using the AIDLC Admin Portal
          </p>
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            <Badge variant="info">AIDLC v2.0.0</Badge>
            <Badge variant="success" dot>Start Here</Badge>
          </div>
        </div>
        <div className="text-right">
          <p className="text-[10px] text-gray-400">Last Updated</p>
          <p className="text-xs font-medium text-gray-600">2026-02-13</p>
          <p className="text-[10px] text-gray-400 mt-1">Authors</p>
          <p className="text-xs text-gray-600">Dan (Ops), Maya (UI), Elena (Docs)</p>
        </div>
      </div>

      {/* Section Navigation */}
      <div className="flex gap-1 border-b border-gray-200 overflow-x-auto">
        {SECTIONS.map((section) => (
          <button
            key={section.id}
            onClick={() => setActiveSection(section.id)}
            className={`flex items-center gap-2 px-3 py-2 text-xs font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeSection === section.id
                ? 'border-ldaja-blue text-ldaja-blue'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {section.icon}
            {section.title}
          </button>
        ))}
      </div>

      {/* Section Content */}
      <div>
        {ActiveContent && <ActiveContent />}
      </div>

      {/* Footer */}
      <div className="text-center py-4 border-t border-gray-100">
        <p className="text-[10px] text-gray-400">
          AIDLC Admin Portal User Guide maintained by Elena (Docs), Dan (Ops), and Maya (UI). For updates, use the Chat Interface or contact the agent team.
        </p>
      </div>
    </div>
  );
}
